import { Card } from '@/components/ui/card';
import { ResponsiveContainer, LineChart, Line } from 'recharts';

/**
 * Una cifra de dirección: el número de hoy, contra el mes anterior, y su serie.
 *
 * **La cifra sola no es una lectura de dirección.** "Cumplimiento 82 %" no dice nada:
 * lo que se decide con eso es si viene subiendo o bajando, y eso lo dan el delta y la
 * línea. Es la diferencia entre el tablero operativo —que contesta *qué pasa ahora*— y
 * el de dirección, que contesta *hacia dónde va*.
 *
 * `mejorEs` existe porque el color no puede deducirse del signo: que suban los casos
 * abiertos es malo y que suba el cumplimiento es bueno. Pintar todo lo que sube de verde
 * es el error clásico de estos tableros.
 */
export default function CifraConTendencia({
  etiqueta, valor, sufijo = '', serie = [], mejorEs = 'alto', nota, vacio = 'sin datos',
}) {
  const limpia = (serie || []).filter((p) => p && p.v != null);
  const hayDato = valor != null;

  // El delta se calcula contra el punto anterior de la propia serie, no contra un valor
  // que el backend tendría que preparar aparte: dos fuentes para el mismo número se
  // desincronizan, y acá la serie ya está.
  let delta = null;
  if (limpia.length >= 2) {
    const ultimo = limpia[limpia.length - 1].v;
    const previo = limpia[limpia.length - 2].v;
    if (previo != null && ultimo != null) delta = ultimo - previo;
  }

  const neutro = delta == null || delta === 0;
  const mejora = mejorEs === 'alto' ? delta > 0 : delta < 0;
  const tono = neutro ? 'text-zinc-400' : mejora ? 'text-[#00A34A]' : 'text-brand-danger';
  const flecha = neutro ? '·' : delta > 0 ? '▲' : '▼';

  return (
    <Card className="border border-brand-border bg-white rounded-sm p-4">
      <p className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">{etiqueta}</p>
      <div className="flex items-end justify-between gap-2 mt-1">
        <p className="text-2xl font-semibold text-[#09090B] leading-none">
          {hayDato ? <>{valor}<span className="text-base text-zinc-400">{sufijo}</span></>
                   : <span className="text-base text-zinc-300">{vacio}</span>}
        </p>
        {delta != null && (
          <span className={`text-[11px] font-medium ${tono}`} title="contra el mes anterior">
            {flecha} {Math.abs(Math.round(delta * 10) / 10)}{sufijo}
          </span>
        )}
      </div>
      {/* La línea es contexto, no un gráfico: sin ejes ni tooltip. Quien quiera el detalle
          lo tiene abajo, en la franja que corresponde. */}
      <div className="h-8 mt-2 -mx-1">
        {limpia.length >= 2 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={limpia}>
              <Line type="monotone" dataKey="v" stroke="#0B2E6B" strokeWidth={1.5} dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center">
            <span className="text-[10px] text-zinc-300">
              {limpia.length === 1 ? 'un solo mes: todavía no hay tendencia' : ''}
            </span>
          </div>
        )}
      </div>
      {nota && <p className="text-[10px] text-zinc-400 mt-1">{nota}</p>}
    </Card>
  );
}
